import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CUSTOMER_WEB_URL } from "../api/apiConfig";
import { useAuth } from "../context/AuthContext";
import useBrandingLogo from "../hooks/useBrandingLogo";
import "../styles/admin.css";

const ADMIN_ROLES = new Set(["technical_admin", "admin", "staff"]);

function redirectToUnifiedLogin() {
  window.location.replace(`${CUSTOMER_WEB_URL}/login`);
}

function parseLoginHandoff() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = params.get("token") || "";
  const rawUser = params.get("user") || "";

  if (!token || !rawUser) return null;

  try {
    return { token, user: JSON.parse(rawUser) };
  } catch {
    return null;
  }
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const handledHandoffRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting] = useState(false);
  const logoUrl = useBrandingLogo();

  useEffect(() => {
    if (handledHandoffRef.current) return;
    handledHandoffRef.current = true;

    const handoff = parseLoginHandoff();

    if (!handoff) {
      redirectToUnifiedLogin();
      return;
    }

    if (!ADMIN_ROLES.has(String(handoff.user?.role || "").toLowerCase())) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.replace(CUSTOMER_WEB_URL);
      return;
    }

    login(handoff);
    window.history.replaceState(null, "", "/login");
    navigate(handoff.user.mustChangePassword ? "/change-password" : "/dashboard", {
      replace: true,
    });
  }, [login, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    redirectToUnifiedLogin();
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-shell">
        <div className="admin-login-visual">
          <div className="admin-login-visual__overlay" />

          <div className="admin-login-brand">
            <img
              src={logoUrl}
              alt="FleetX Logo"
              className="admin-login-brand__logo"
            />
            <span>CarFleet Admin</span>
          </div>

          <div className="admin-login-visual__content">
            <p className="admin-login-visual__eyebrow">
              Fleet Management System
            </p>

            <h1>Drive Your Business Forward</h1>

            <p className="admin-login-visual__text">
              Complete control over your rental fleet, vehicles, bookings,
              customers, and analytics, all in one powerful dashboard.
            </p>

            <div className="admin-login-stats">
              <div>
                <strong>120+</strong>
                <span>Vehicles Managed</span>
              </div>
              <div>
                <strong>2,400+</strong>
                <span>Bookings Processed</span>
              </div>
              <div>
                <strong>98%</strong>
                <span>Customer Satisfaction</span>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-login-formPane">
          <div className="admin-login-formWrap">
            <p className="admin-login-form__eyebrow">Welcome Back</p>
            <h2>Sign in to Dashboard</h2>
            <p className="admin-login-form__subtitle">
              Access your fleet management system
            </p>

            <form onSubmit={handleLogin} className="admin-login-form">
              <label className="admin-login-label">Email Address</label>
              <div className="admin-login-inputWrap">
                <span className="admin-login-inputIcon">@</span>
                <input
                  className="admin-login-input"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              <label className="admin-login-label">Password</label>
              <div className="admin-login-inputWrap">
                <span className="admin-login-inputIcon">#</span>
                <input
                  className="admin-login-input admin-login-input--password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="admin-login-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              <div className="admin-login-row">
                <Link to="/forgot-password" className="admin-login-forgot">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="admin-login-button"
                disabled={submitting}
              >
                {submitting ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {msg ? <p className="admin-login-error">{msg}</p> : null}

            <div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
